import { Button } from '@tablecheck/tablekit-react';
import { AlertAppearance } from '@tablekit/alert';
import queryString from 'query-string';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { PasswordField } from 'components/PasswordField';
import { PasswordValidationsProgress } from 'components/PasswordField/PasswordRequirements';
import { validatePasswordRequirements } from 'components/PasswordField/utils';
import { Redirecting } from 'components/Redirecting';
import { SignInLink } from 'components/SignInLink';
import {
  getInitialState,
  resetPasswordPageReducer
} from 'pages/ResetPassword/resetPasswordPageReducer';
import { resetPasswordRequest } from 'pages/ResetPassword/resetPasswordRequest';
import { usePasswordRequirements } from 'pages/ResetPassword/usePasswordRequirements';
import { AuthFormWrapper, FormAlert, FormFooter } from 'utils/form.styles';
import { onInputFocus } from 'utils/input';
import { parseError } from 'utils/parseError';

import { ErrorsList, PasswordMismatchAlert } from './styled';

function ErrorsAlert({ errors }: { errors: string[] }) {
  const [t] = useTranslation();

  return (
    <FormAlert
      appearance={AlertAppearance.Danger}
      isShow
      shouldUsePortal={false}
      isDismissible={false}
      shouldHideIcon
    >
      <>
        <h4>{t('forms.errors')}</h4>
        <ErrorsList>
          {errors.map((error) => (
            <li key={error}>{t(error)}</li>
          ))}
        </ErrorsList>
      </>
    </FormAlert>
  );
}

function MissingTokenAlert() {
  const [t] = useTranslation();

  return (
    <FormAlert
      appearance={AlertAppearance.Danger}
      id="error"
      isShow
      data-test-id="alert"
      shouldUsePortal={false}
      isDismissible={false}
    >
      {t('forms.missing_token')}
    </FormAlert>
  );
}

function SuccessAlert({
  data
}: {
  data: { location?: string; action?: string };
}) {
  const [t] = useTranslation();

  return (
    <>
      <FormAlert
        appearance={AlertAppearance.Success}
        isShow
        shouldUsePortal={false}
        isDismissible={false}
      >
        {t('forms.password_reset_success')}
      </FormAlert>
      <Redirecting data={data} />
    </>
  );
}

export function ResetPasswordPage(): JSX.Element {
  const [t, { language }] = useTranslation();
  const location = useLocation();
  const [
    {
      isLoading,
      password,
      isDirtyPasswordField,
      invalidKey,
      passwordConfirm,
      isDirtyPasswordConfirmField,
      isPasswordMatching,
      data,
      errors
    },
    dispatch
  ] = React.useReducer(resetPasswordPageReducer, undefined, () =>
    getInitialState()
  );
  const { username, token } = queryString.parse(location.search);
  const { passwordRequirements, email } = usePasswordRequirements(token);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    dispatch({ type: 'SET_LOADING' });
    resetPasswordRequest({ password, token, locale: language })
      .then((responseData) => {
        dispatch({ type: 'SET_DATA', payload: { data: responseData } });
      })
      .catch((error) => {
        const responseErrors = [];
        if (
          error.response.data.error === 'password_invalid' &&
          error.response.data.messages
        ) {
          error.response.data.messages.forEach((message: string) => {
            responseErrors.push(message);
          });
        } else {
          responseErrors.push(parseError(error.response.data.error, true));
        }
        dispatch({ type: 'SET_ERRORS', payload: { errors: responseErrors } });
      });
  };

  return (
    <AuthFormWrapper onSubmit={onSubmit}>
      <h2>{t('forms.reset_password')}</h2>
      {!data && errors.length > 0 ? <ErrorsAlert errors={errors} /> : null}
      {username && (
        <p>
          <strong>{t('forms.username')}:</strong> {username}
        </p>
      )}
      {data ? <SuccessAlert data={data} /> : null}
      {!token ? <MissingTokenAlert /> : null}
      {!data ? (
        <>
          <PasswordField
            name="password"
            id="password"
            onChangeValue={(value, metadata) => {
              dispatch({
                type: 'SET_PASSWORD',
                payload: {
                  password: value,
                  invalidKey: metadata.invalidKey
                }
              });
            }}
            onFocus={onInputFocus}
            onBlur={() => {
              if (password) {
                dispatch({ type: 'SET_PASSWORD_DIRTY' });
              }
            }}
            value={password}
            label={t('forms.new_password')}
            validationFunc={(value) =>
              validatePasswordRequirements({
                value,
                email,
                passwordRequirements
              })
            }
            required
            data-variant={isLoading ? 'disabled' : undefined}
          />
          <PasswordField
            name="password-confirm"
            id="password-confirm"
            onChangeValue={(value: string) => {
              dispatch({
                type: 'SET_PASSWORD_CONFIRM',
                payload: {
                  passwordConfirm: value
                }
              });
            }}
            onFocus={onInputFocus}
            onBlur={() => {
              if (passwordConfirm) {
                dispatch({ type: 'SET_PASSWORD_CONFIRM_DIRTY' });
              }
            }}
            value={passwordConfirm}
            label={t('forms.confirm_new_password')}
            required
            data-variant={isLoading ? 'disabled' : undefined}
          />
          <PasswordValidationsProgress
            value={password}
            email={email}
            passwordRequirements={passwordRequirements}
            isDirty={isDirtyPasswordField}
          />
          {isDirtyPasswordConfirmField && !isPasswordMatching && (
            <PasswordMismatchAlert
              id="resetPasswordMismatch"
              data-variant="error"
            >
              {t('forms.password_mismatch')}
            </PasswordMismatchAlert>
          )}
          <Button
            data-varinat="primary"
            data-size="large"
            disabled={
              isLoading ||
              !password ||
              !passwordConfirm ||
              !isPasswordMatching ||
              !!invalidKey
            }
            aria-busy={isLoading}
            type="submit"
            aria-label="Submit button"
          >
            {t('forms.reset_password')}
          </Button>
          <FormFooter>
            <SignInLink data-test-id="sign-in-link" />
          </FormFooter>
        </>
      ) : null}
    </AuthFormWrapper>
  );
}
